import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@bloomcode/ui';
import { ChevronLeft, ChevronRight, CreditCard } from '@bloomcode/icons';
import { usePayments } from '@/hooks';
import type { PaymentStatus } from '@bloomcode/graphql';

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(
    amount / 100
  );
}

function paymentStatusVariant(status: string): 'default' | 'success' | 'destructive' | 'warning' {
  switch (status) {
    case 'PAID':
    case 'SUCCEEDED':
      return 'success';
    case 'FAILED':
      return 'destructive';
    case 'PENDING':
      return 'warning';
    default:
      return 'default';
  }
}

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: 'ALL' },
  { label: 'Succeeded', value: 'PAID' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Failed', value: 'FAILED' },
];

export default function PaymentsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const { payments, pagination, loading, error } = usePayments({
    page,
    limit: 20,
    status: statusFilter !== 'ALL' ? (statusFilter as PaymentStatus) : undefined,
  });

  return (
    <>
      <Helmet>
        <title>Payment History | Bloomcode Ledger Portal</title>
        <meta name="description" content="View your complete payment history." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-[Manrope]">Payment History</h1>
            <p className="text-muted-foreground mt-1">All payments made on your account</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {pagination
                ? `${pagination.total} payment${pagination.total !== 1 ? 's' : ''}`
                : 'Payments'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="p-8 text-center text-destructive">
                Failed to load payments. Please try again.
              </div>
            ) : loading ? (
              <div className="divide-y">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-4">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-16" />
                  </div>
                ))}
              </div>
            ) : payments.length === 0 ? (
              <div className="p-12 text-center">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No payments found.</p>
              </div>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-4 px-6 py-3 bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <span>Date</span>
                  <span>Invoice #</span>
                  <span>Amount</span>
                  <span>Method</span>
                  <span>Status</span>
                </div>
                <div className="divide-y">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-2 md:gap-4 px-6 py-4 items-center"
                    >
                      <span className="text-sm text-muted-foreground">
                        {payment.created_at
                          ? new Date(payment.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </span>
                      <span className="text-sm font-medium">
                        {payment.invoice_id ? `#${payment.invoice_id.slice(-8)}` : '—'}
                      </span>
                      <span className="text-sm font-semibold">
                        {formatCurrency(payment.amount, payment.currency)}
                      </span>
                      <span className="text-sm text-muted-foreground capitalize">
                        {payment.provider || '—'}
                      </span>
                      <Badge variant={paymentStatusVariant(payment.status)} className="w-fit">
                        {payment.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.pages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasPrev}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
